import { jsx } from '@emotion/react';
import { useState } from 'react';
import { Animation, Avatar, FloatingBox, useClickOutside, } from '@zapier/design-system';
import { Tooltip } from '@zapier/design-system-beta';
import { labelStyles } from '@zapier/style-encapsulation';
import { createUniqueClassName } from '../../utils/createUniqueClassName';
import UniversalNavLink from '../UniversalNavLink';
import { AccountsMenuLinks } from '../../constants';
import { baseReset, boxSizing } from '../../../utils/style-mixins/reset';
const detailsClassName = createUniqueClassName('AccountsMenu', 'details');
const MENU_ITEM_HEIGHT = '48px';
const Styles = labelStyles('AccountsMenu', {
    root: Object.assign(Object.assign({}, boxSizing), { position: 'relative' }),
    accounts: Object.assign(Object.assign({}, boxSizing), { maxHeight: `calc(${MENU_ITEM_HEIGHT} * 3.5)`, overflowY: 'auto', '& > a': {
            padding: 'var(--zds-space-4, 4px) var(--zds-space-8, 8px) !important',
        } }),
    details: Object.assign({}, baseReset),
    summary: Object.assign(Object.assign({}, baseReset), { position: 'relative', display: 'block', cursor: 'pointer', transition: Animation.transitionValue, '&::-webkit-details-marker': {
            display: 'none',
        }, '& div': {
            transition: 'none',
        }, '&:focus-visible': {
            outline: 'var(--zds-border-focus)',
            borderRadius: 'var(--zds-radius-pill)',
            outlineOffset: 1,
            transition: 'none',
        } }),
    divider: Object.assign(Object.assign({}, boxSizing), { margin: 'var(--zds-space-8, 8px) 0', height: 1, backgroundColor: 'var(--zds-stroke-weaker, #D7D3C9)', border: 'none' }),
    menuContainer: Object.assign(Object.assign({}, boxSizing), { padding: 'var(--zds-space-8, 8px)', width: 278 }),
    email: Object.assign(Object.assign({}, boxSizing), { display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '0 var(--zds-space-8, 8px)', font: `var(--zds-small-print-1)`, color: 'var(--zds-text-weaker, #574E4C)', marginBottom: 'var(--zds-space-8, 8px)' }),
    emailText: Object.assign(Object.assign({}, boxSizing), { whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }),
});
const renderAccountSwitchMenu = (accounts, currentAccountId, onAccountClick, setIsMenuOpen) => {
    return (jsx("div", { css: Styles.accounts, "data-zds": true }, accounts.map((account) => {
        let secondaryText = 'Individual';
        const isMultiUserAccount = account.hasTeams;
        if (isMultiUserAccount) {
            secondaryText = 'Organization';
        }
        if (account.parentAccount &&
            account.parentAccountRelationship === 'workspace') {
            secondaryText = `Workspace - ${account.parentAccount.name}`;
        }
        const isCurrentAccount = account.id === currentAccountId;
        return (jsx(UniversalNavLink, { isV3Link: true, href: account.accountSwitcherUrl, isSelected: isCurrentAccount, key: account.id, onClick: (event) => {
                if (onAccountClick) {
                    onAccountClick(account, event);
                }
                setIsMenuOpen(false);
            }, renderBeforeText: () => (jsx("div", { css: Styles.avatar, "data-zds": true },
                jsx(Avatar, { isBlock: true, isSelected: false, name: account.name, shouldLazilyLoad: false, size: "medium", url: account.imageUrl }))), role: "menuitem", text: account.name, secondaryText: secondaryText, showCheckmarkOnSelected: true }));
    })));
};
const renderMenuLinks = (menuLinks, setIsMenuOpen) => {
    return menuLinks.map((menuLink) => (jsx(UniversalNavLink, { isV3Link: true, href: menuLink.href, icon: typeof menuLink.icon === 'object'
            ? menuLink.icon.selected
            : menuLink.icon, key: menuLink.text, onClick: () => {
            if (menuLink.onClick) {
                menuLink.onClick();
            }
            setIsMenuOpen(false);
        }, role: "menuitem", text: menuLink.text, target: menuLink.target })));
};
const fallbackAccount = {
    id: -1,
    name: '⋯',
    accountSwitcherUrl: '/',
    hasTeams: false,
    parentAccountRelationship: null,
    parentAccount: null,
};
const AccountsMenu = ({ accounts, email, currentAccountId = undefined, hasWhiteBackground = undefined, menuLabel = 'Accounts Settings', menuLinks = AccountsMenuLinks, onAccountClick = undefined, size = 'medium', }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [setClickOutsideNode] = useClickOutside({
        onClickOutside: () => setIsMenuOpen(false),
    });
    const currentAccount = accounts.find((account) => account.id === currentAccountId) ||
        fallbackAccount;
    const toggleMenu = (event) => {
        event.preventDefault();
        setIsMenuOpen(!isMenuOpen);
    };
    return (jsx("div", { css: Styles.root, "data-zds": true },
        jsx("details", { className: detailsClassName, css: Styles.details, "data-testid": "accountsMenu", open: isMenuOpen, ref: (node) => setClickOutsideNode(node), "data-zds": true },
            jsx(Tooltip, { content: currentAccount.name, placement: "left" },
                jsx(Tooltip.FocusableTrigger, { "aria-haspopup": "menu", "aria-label": menuLabel, css: Styles.summary, "data-testid": "accounts-menu-summary", onClick: toggleMenu, tag: "summary" },
                    jsx(Avatar, { isBlock: true, isInteractive: true, name: currentAccount.name, shouldLazilyLoad: false, size: size === 'small' ? 'small' : 'medium', url: currentAccount.imageUrl }))),
            jsx(FloatingBox, { align: "right", hasWhiteBackground: hasWhiteBackground, position: "south", borderRadius: "small" },
                jsx("div", { css: Styles.menuContainer, role: "menu", "data-zds": true },
                    jsx("div", { css: Styles.email, "data-zds": true },
                        jsx("span", { css: Styles.emailText, "data-zds": true }, email)),
                    renderAccountSwitchMenu(accounts, currentAccountId, onAccountClick, setIsMenuOpen),
                    jsx("hr", { css: Styles.divider }),
                    renderMenuLinks(menuLinks, setIsMenuOpen))))));
};
export default AccountsMenu;
//# sourceMappingURL=AccountsMenu.js.map
// @zapier/universal-layout@12.0.37 (fb08e5a7f)
